<template>
  <v-container>
    <h3 class="page-title">二階報到作業</h3>
    <v-row>
      <v-col cols="6" sm="3">
        <v-autocomplete
          v-model="selected.行政區"
          outlined
          :items="衛生所"
          label="負責衛生所"
          dense
          hide-details
          :disabled="!!this.$store.state.user.行政區"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6" sm="3">
        <v-autocomplete
          v-model.number="selected.年度"
          :items="年度"
          label="年度"
          outlined
          dense
          hide-details
        ></v-autocomplete>
      </v-col>
      <v-col cols="6" sm="3">
        <v-autocomplete
          v-model="selected.場次"
          :items="uniqSections"
          label="場次"
          no-data-text="請輸入關鍵字"
          outlined
          dense
          hide-details
          @update:search-input="searchSection"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6" sm="3">
        <v-select
          v-model="selected.篩選條件"
          :items="['全部', '已回檢']"
          label="篩選條件"
          outlined
          dense
          hide-details
        ></v-select>
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          v-model="selected.關鍵字"
          label="關鍵字"
          outlined
          dense
          hide-details
          clearable
        >
          <v-icon slot="append" @click="load">
            mdi-magnify
          </v-icon></v-text-field
        >
      </v-col>
      <v-col cols="5" sm="5">
        <v-menu offset-y open-on-hover>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mr-4" color="info" v-bind="attrs" v-on="on">
              下載資料
            </v-btn>
          </template>
          <v-list>
            <v-list-item link>
              <v-list-item-title @click="exportList">
                場次回檢列表
              </v-list-item-title>
            </v-list-item>
            <v-list-item link>
              <v-list-item-title @click="exportPrint('m')">
                列印轉診單
              </v-list-item-title>
            </v-list-item>
            <v-list-item link>
              <v-list-item-title @click="exportPrint('s')">
                列印複檢單
              </v-list-item-title>
            </v-list-item>
            <v-list-item link v-if="!isHospital">
              <v-list-item-title @click="exportQuestion">
                匯出整篩問卷
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn color="primary" @click="loadList">重新讀取</v-btn>
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="ml-auto text-right mt-1 text-body-1 font-weight-bold"
      >
        <div class="d-inline mr-4">已預約：{{ list.length }}</div>
        <div class="d-inline mr-4">
          已回檢：{{ list.filter((v) => v.回檢).length }}
        </div>
      </v-col>
    </v-row>
    <div class="mt-4">
      <v-data-table
        :headers="headers"
        :items="filterList"
        no-data-text="尚無預約"
        :mobile-breakpoint="0"
        :footer-props="{
          itemsPerPageText: '每頁幾筆',
          itemsPerPageOptions: [10, 30, -1],
        }"
      >
        <template v-slot:item.序號="{ index }"> {{ index + 1 }} </template>
        <template v-slot:item.篩檢項目="{ item }">
          {{ Object.keys(lodash.pickBy(item.篩檢項目, (o) => o)).join("、") }}
        </template>
        <template v-slot:item.report="{ item }">
          <div v-if="!item.report[0].外部問卷">未填</div>
        </template>
        <template v-slot:item.報到號碼="{ item }">
          {{ item.報到號碼 || "-" }}
        </template>
        <template v-slot:item.已報到="{ item }">
          {{ item.已報到 ? "" : "無" }}
        </template>
        <template v-slot:item.抽血="{ item }">
          {{ lodash.isEmpty(item.血液報告) ? "無" : "" }}
        </template>
        <template v-slot:item.年齡="{ item }">
          {{ age(item.出生年月日) }}
        </template>

        <template v-slot:item.回檢="{ item }">
          <v-simple-checkbox
            @input="toggleCheckin(item)"
            :off-icon="
              item.回檢 === null
                ? 'mdi-crop-free'
                : 'mdi-checkbox-blank-outline'
            "
            v-model="item.回檢"
            :ripple="false"
          ></v-simple-checkbox>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="printDialog" v-if="printDialog" width="auto">
      <v-card class="text-center pa-5">
        <PrintDialog
          :printType="printType"
          :list="list"
          :section="lodash.find(sections, selected.場次)"
          @loadList="loadList"
        ></PrintDialog>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import barcodeReader from "@wrurik/barcode-scanner";
import downloadCsv from "download-csv";
import { 行政區, 站點 } from "@/libs/const";
import dayjs from "dayjs";
import _ from "lodash";
import PrintDialog from "@/components/Print/dialog_t2";
const all行政區 = {
  text: "全部",
  value: null,
};
const 年度 = dayjs().get("year") - 1911;
export default {
  name: "CheckinReport",
  components: {
    PrintDialog,
  },
  data: () => ({
    headers: [
      { text: "序號", value: "序號" },
      { text: "報到號碼", value: "報到號碼" },
      { text: "姓名", value: "姓名" },
      { text: "身分證字號", value: "身分證字號" },
      { text: "出生年月日", value: "出生年月日" },
      { text: "年齡", value: "年齡" },
      { text: "電話", value: "電話", sortable: false },
      { text: "無報到", value: "已報到" },
      { text: "無抽血", value: "抽血" },
      // { text: "篩檢項目", value: "篩檢項目", sortable: false },
      // { text: "站點狀態", value: "站點", sortable: false },

      { text: "回檢", value: "回檢" },
      { text: "回檢時間", value: "回檢時間", width: "170px" },
    ],
    sectionFilter: null,
    cancelScan: null,
    sections: [],
    list: [],
    printType: "s",
    printDialog: false,
    selected: {
      行政區: all行政區,
      年度,
      場次: null,
      篩選條件: "全部",
      關鍵字: "",
    },
    站點: Object.keys(站點),
    lodash: _,
  }),
  computed: {
    isHospital() {
      return ["醫療院所"]?.includes(this.$store.state.user.角色);
    },
    stationURL() {
      let section = _.find(this.sections, this.selected.場次);
      let url = `${window.location.origin}/station/${section?._id}`;
      return url;
    },
    filterList() {
      let list = [...this.list];
      if (this.selected.關鍵字) {
        list = list.filter((person) => {
          return (
            person.姓名?.includes(this.selected.關鍵字) ||
            person.身分證字號?.includes(this.selected.關鍵字) ||
            person.出生年月日.toString()?.includes(this.selected.關鍵字) ||
            person.電話?.includes(this.selected.關鍵字)
          );
        });
      }
      switch (this.selected.篩選條件) {
        case "已回檢":
          list = list.filter((person) => person.回檢);
          break;
        // case "已完成":
        //   list = list.filter((person) => !!person.完成時間);
        //   break;
        // case "成人健康檢查":
        //   list = list.filter((person) => person.篩檢項目.成健);
        //   break;
        // case "口腔篩檢":
        //   list = list.filter((person) => person.篩檢項目.口腔);
        //   break;
        // case "子宮頸抹片檢查":
        //   list = list.filter((person) => person.篩檢項目.子抹);
        //   break;
        // case "乳房X光攝影檢查":
        //   list = list.filter((person) => person.篩檢項目.乳攝);
        //   break;
        // case "糞便潛血檢查":
        //   list = list.filter((person) => person.篩檢項目.大腸);
        //   break;
        // case "未填線上問卷":
        //   list = list.filter((person) => !person.report[0].外部問卷);
        //   break;
      }
      return list;
    },
    衛生所() {
      let place = 行政區.map((v) => ({
        text: v + "衛生所",
        value: v,
      }));
      place.unshift(all行政區);
      return place;
    },
    年度() {
      let years = [年度 + 1];
      for (let n = 0; n <= 3; n++) {
        years.push(年度 - n);
      }
      return years;
    },
    uniqSections() {
      if (!this.sectionFilter || this.sectionFilter.length < 2) {
        return [];
      }
      let sections = this.sections.map((row) => ({
        text: `${dayjs(row.日期).format("MM/DD")} ${row.地點}`,
        value: {
          日期: row.日期,
          地點: row.地點,
        },
      }));
      sections = sections.filter((row) =>
        row.text.includes(this.sectionFilter),
      );
      sections = _.uniqWith(sections, _.isEqual);
      sections = _.sortBy(sections, ["text"]);

      return sections;
    },
  },
  async created() {
    if (this.$store.state.user.行政區) {
      this.selected.行政區 = this.衛生所.find(
        (row) => row.value == this.$store.state.user.行政區,
      );
    }
    await this.load();
    this.cancelScan = barcodeReader.onScan(this.scanHandler, {
      scanDuration: 500,
    });
  },
  beforeDestroy() {
    this.cancelScan();
  },

  methods: {
    searchSection(search) {
      this.sectionFilter = search;
    },
    age(date) {
      let birth = parseInt(date) + 19110000 + "";
      let year = dayjs(birth).get("year");
      let thisYear = dayjs().get("year");
      return thisYear - year;
    },
    isSelectedSection() {
      if (!this.selected.場次) {
        this.$toast.error("尚未選擇場次");
        return false;
      }
      return true;
    },
    exportList() {
      if (!this.isSelectedSection()) {
        return false;
      }
      let data = [...this.list];
      data = data.map((row) => {
        let { 報到號碼, 姓名, 身分證字號, 出生年月日, 電話, 已回檢, 回檢時間 } =
          row;
        return {
          報到號碼,
          姓名,
          身分證字號,
          出生年月日,
          電話,
          已回檢: 已回檢 ? "是" : "否",
          回檢時間,
        };
      });
      downloadCsv(
        this.sanitizeCsvRows(data),
        {},
        `${this.selected.場次.日期} ${this.selected.場次.地點}預約回檢列表`,
      );
    },
    mapSum(r, keys) {
      let sum = 0;
      keys.forEach((key) => {
        sum += parseInt(r.report?.[0]?.外部問卷?.[key]?.split(".")[0] || 0);
      });
      return sum;
    },
    mapping(r, key, matchValue = null, trueValue = "", falseValue = "") {
      let value = r.report?.[0]?.外部問卷?.[key]?.split(".")[0] || "";
      if (!matchValue) return value;
      if (value === matchValue) {
        return trueValue || value;
      } else {
        return falseValue;
      }
    },
    plusSum(r, keys) {
      let sum = 0;
      keys.forEach((key) => {
        let value = r.report?.[0]?.外部問卷?.[key]?.split(".")[0] || -1;
        value = parseInt(value) + 1;
        sum += value;
      });
      return sum;
    },
    plusMapping(r, key) {
      let value = r.report?.[0]?.外部問卷?.[key]?.split(".")[0] || "";
      if (!value) return "";
      return (parseInt(value) + 1).toString();
    },
    escapeCsvValue(value) {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (/[",\r\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    },
    sanitizeCsvRows(rows) {
      return rows.map((row) => {
        const next = {};
        Object.keys(row).forEach((key) => {
          next[key] = this.escapeCsvValue(row[key]);
        });
        return next;
      });
    },
    mappingQuestion(
      r,
      key,
      matchValue = null,
      trueValue = "",
      falseValue = "",
    ) {
      let value = r.report?.[0]?.滿意問卷?.[key]?.split(".")[0] || "";
      if (!matchValue) return value;
      if (value === matchValue) {
        return trueValue || value;
      } else {
        return falseValue;
      }
    },
    mappingOption(r, key) {
      let map = {
        完全沒有: "0",
        輕微: "1",
        中等程度: "2",
        厲害: "3",
        非常厲害: "4",
        "": "",
      };
      return map[r.report?.[0]?.外部問卷?.[key] || ""];
    },
    parseOptionCodes(raw) {
      if (!raw) return [];
      return String(raw)
        .split(/[,，]/)
        .map((item) => item.trim().split(".")[0]?.toUpperCase())
        .filter(Boolean);
    },
    hasOptionCode(問卷, key, ...codes) {
      const selectedCodes = this.parseOptionCodes(問卷?.[key]);
      const targets = codes.flat().map((code) => String(code).toUpperCase());
      return targets.some((code) => selectedCodes.includes(code));
    },
    exportQuestion() {
      if (!this.isSelectedSection()) {
        return false;
      }
      let data = [...this.list];
      data = data.filter((r) => r.report[0].id93 && r.已報到);

      data = data.map((r) => {
        _.each(r, (value, key) => {
          if (value === "0") {
            外部問卷[key] = "";
          }
        });

        let 外部問卷 = r.report[0].外部問卷 || {};
        return {
          SV_ID: r.report[0].id93,
          SMOKE: this.mapping(r, "1.請問您目前有無吸菸?"),
          QUITSMOKETIME: this.mapping(r, "1-1.已戒菸"),
          QUITSMOKEWILL: this.mapping(
            r,
            "1-2.如果您有吸菸，請問您是否有戒菸的意願？",
          ),
          E_CIGARETTE: this.mapping(r, "2.請問您目前有無使用電子菸?"),
          "E_CIGARETTE_SOURCE-0": this.mapping(
            r,
            "2-1.請問電子煙來源是?",
            "0",
            "0",
          ),
          "E_CIGARETTE_SOURCE-1": this.mapping(
            r,
            "2-1.請問電子煙來源是?",
            "1",
            "1",
          ),
          "E_CIGARETTE_SOURCE-2": this.mapping(
            r,
            "2-1.請問電子煙來源是?",
            "2",
            "2",
          ),
          "E_CIGARETTE_SOURCE-3": this.mapping(
            r,
            "2-1.請問電子煙來源是?",
            "3",
            "3",
          ),
          E_CIGARETTE_OTHER: this.mapping(r, "其他電子菸來源？"),
          "E_NETWORK_SOURCE-0": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "0",
            "0",
          ),
          "E_NETWORK_SOURCE-1": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "1",
            "1",
          ),
          "E_NETWORK_SOURCE-2": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "2",
            "2",
          ),
          "E_NETWORK_SOURCE-3": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "3",
            "3",
          ),
          "E_NETWORK_SOURCE-4": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "4",
            "4",
          ),
          "E_NETWORK_SOURCE-5": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "5",
            "5",
          ),
          "E_NETWORK_SOURCE-6": this.mapping(
            r,
            "2-2.如為網路平台，請問由何種平台購入?",
            "6",
            "6",
          ),
          E_NETWORK_OTHER: this.mapping(r, "其他網路平台為?"),
          SECONDHAND: this.mapping(r, "3.請問您是否覺得吸入二手菸?"),
          DRINK: this.mapping(r, "4.請問您目前有無喝酒?"),
          CHEWNUTS: this.mapping(r, "5.請問您目前有無嚼檳榔?"),
          BRUSHTOOTH: this.mapping(r, "6.請問您是否有刷牙的習慣？"),
          WEIGHTCHECK: this.mapping(r, "7.請問您是否有每月量體重的習慣？"),
          EXERCISE: this.mapping(
            r,
            "8.請問您有無這樣活動(例如：散步、健走或打掃、伸展)，每次持續10分鐘以上？",
          ),
          EXERCISEABOUT30: this.mapping(r, "1-1.每天分段累積30分鐘以上(含)？"),
          EXERCISE5TIMES: this.mapping(r, "1-2.每週5次以上？"),
          DRIVE: this.mapping(r, "9.請問您開車或騎車或乘坐汽機車嗎?"),
          HELMET: this.mapping(r, "9-1.您有使用安全帶或戴安全帽的情形是："),
          AFTERDRINK: this.mapping(
            r,
            "9-2.假如您參加應酬喝酒後，您會：【不喝酒者免填】",
          ),
          COUGH: this.mapping(r, "10.您是否出現咳嗽超過二週的情形？"),
          // NOHOPE: this.mapping(
          //   r,
          //   "11.過去兩週，您是否感覺情緒低落、沮喪或沒有希望？"
          // ),
          // NOINTEREST: this.mapping(
          //   r,
          //   "12.過去兩週，您是否感覺做事失去興趣或樂趣？"
          // ),
          FALL:
            this.mapping(r, "13.過去一年內是否跌倒？") ||
            this.mapping(r, "11.過去一年內是否跌倒？"),
          SightBrother:
            this.mapping(
              r,
              "14.整體而言，您的視力問題是否造成日常生活上的困擾？",
            ) ||
            this.mapping(
              r,
              "12.整體而言，您的視力問題是否造成日常生活上的困擾？",
            ),
          EATVEGETABLE: this.mapping(r, "1.請問您平均每天吃幾份蔬菜？"),
          EATFRUIT: this.mapping(r, "2.請問您平均每天吃幾份水果？"),
          EATCHMED: this.mapping(
            r,
            "3.請問您目前是否有服用中(草)藥的習慣？(不包含中醫師開立之藥方，最近一個月內)",
          ),
          STOPCHMEDYEAR: this.mapping(r, "停用幾年？"),
          EATCHMEDPURPOSE: this.mapping(r, "3-1 食用目的："),
          EATCHMEDPUROTHER: this.mapping(r, "其他食用中(草)藥目的？"),
          CHMEDSOURCE: this.mapping(r, "3-2 中(草)藥來源："),
          CHMEDSOURCEOTHER: this.mapping(r, "其他中(草)藥來源？"),
          CHMEDTYPE: this.mapping(r, "3-3 種類："),
          BUYCHMED: this.mapping(r, "4.請問您是否曾經自行購買藥物？"),
          EATDRUGPURPOSE: this.mapping(r, "4-1 食用目的："),
          EATDRUGPURPOSEOTH: this.mapping(r, "其他食用藥物目的？"),
          DRUGSOURCE: this.mapping(r, "4-2 購買來源："),
          DRUGSOURCEOTHER: this.mapping(r, "其他購買藥物來源？"),
          KINDEYDISEASE: this.mapping(
            r,
            "5.請問您知不知道血壓、血糖沒控制好，會引起洗腎(洗腰子)嗎?",
          ),
          SLOWDISEASE: this.mapping(
            r,
            "6.請問您知不知道慢性腎臟疾病與高血壓、糖尿病、痛風、使用一些來路不明的祕方、中草藥或是非醫師處方的止痛藥、抗生素等有關?",
          ),
          KNOWBLOOD: this.mapping(r, "7.請問您知不知道自己的血壓值?"),
          BLOODSTANDARD: this.mapping(
            r,
            "8.請問您認為十八歲以上成人血壓值在什麼範圍內才算標準?",
          ),
          BLOODCHECK: this.mapping(r, "9.請問您過去1年量測血壓的習慣為何?"),
          KNOWSUGER: this.mapping(r, "10.請問您知不知道自己空腹的血糖值?"),
          SUGERSTANDARD: this.mapping(
            r,
            "11.請問您認為十八歲以上成人空腹血糖值在什麼範圍內才算標準?",
          ),
          KNOWBMI: this.mapping(
            r,
            "12.請問您以前有聽過「身體質量指數」或是「BMI」嗎？",
          ),
          CORRECTBMI: this.mapping(
            r,
            "13.請問我國18歲以上成人肥胖是指身體質量指數(BMI)大於25、27還是30？",
          ),
          WAISTSTANDARD: this.mapping(
            r,
            "14.請問您成年男性腰圍在什麼範圍內才算標準?",
          ),
          WAISTSTANDARDF: this.mapping(
            r,
            "15.請問您成年女性腰圍在什麼範圍內才算標準?",
          ),
          DEATHFIRST: this.mapping(
            r,
            "16.請問我國民眾十大死亡原因第一位是哪個疾病？",
          ),
          EARLYFOUND: this.mapping(
            r,
            "17.癌症早期發現與晚期發現的存活率是否有差異？",
          ),
          CHUTNUTS: this.mapping(
            r,
            "18.有一種說法「吃檳榔即使不加配料，也會導致癌症」（配料：包括紅灰、白灰、荖花、荖藤、荖葉…等等），請問您認為這種說法對不對？",
          ),
          STOPNUTS: this.mapping(
            r,
            "19.請問您知道拒檳榔菸酒可以預防口腔癌嗎？",
          ),
          LUNGEXAM: this.mapping(
            r,
            "20.請問您知道政府有提供公費的肺癌篩檢服務嗎？",
          ),
          LUNGEXAM_1: this.mapping(
            r,
            "20-1.您知道公費「肺癌篩檢」的申請條件嗎?",
          ),
          "NOTWHOLEGRAINS-0": this.mapping(
            r,
            "21.下列食物那個不屬於全榖雜糧類?",
            "0",
          ),
          "NOTWHOLEGRAINS-1": this.mapping(
            r,
            "21.下列食物那個不屬於全榖雜糧類?",
            "1",
          ),
          "NOTWHOLEGRAINS-2": this.mapping(
            r,
            "21.下列食物那個不屬於全榖雜糧類?",
            "2",
          ),
          "NOTWHOLEGRAINS-3": this.mapping(
            r,
            "21.下列食物那個不屬於全榖雜糧類?",
            "3",
          ),
          "NOTWHOLEGRAINS-4": this.mapping(
            r,
            "21.下列食物那個不屬於全榖雜糧類?",
            "4",
          ),
          RESOURCEPLATFORM: this.mapping(
            r,
            "22.請問您有使用過" +
              this.$city.name +
              "長者社區資源整合運用平台嗎？",
          ),
          COUGHLEAK: this.mapping(
            r,
            "23.(女性填答)當您用力咳嗽時，是否有漏尿情形?",
          ),
          Syndrome1: this.mapping(r, "24.請問您有聽過代謝症候群嗎?"),
          Syndrome2: this.mapping(
            r,
            "25.您知道5大警戒指標(血壓偏高、空腹血糖偏高、空腹三酸甘油酯偏高、腹部肥胖、高密度脂蛋白膽固醇偏低)，幾項超標即為代謝症候群？",
          ),
          "FAMILYCANCER-1":
            this.mapping(r, "2.癌症部分：-0.無", "1", "1") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "0")
              ? "1"
              : "",
          "FAMILYCANCER-2":
            this.mapping(r, "2.癌症部分：-1.子宮頸癌", "1", "2") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "1")
              ? "2"
              : "",
          "FAMILYCANCER-3":
            this.mapping(r, "2.癌症部分：-2.乳癌", "1", "3") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "2")
              ? "3"
              : "",
          "FAMILYCANCER-4":
            this.mapping(r, "2.癌症部分：-3.肝癌", "1", "4") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "3")
              ? "4"
              : "",
          "FAMILYCANCER-5":
            this.mapping(r, "2.癌症部分：-4.肺癌", "1", "5") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "4")
              ? "5"
              : "",
          "FAMILYCANCER-6":
            this.mapping(r, "2.癌症部分：-5.大腸直腸癌", "1", "6") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "5")
              ? "6"
              : "",
          "FAMILYCANCER-7":
            this.mapping(r, "2.癌症部分：-6.胃癌", "1", "7") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "6")
              ? "7"
              : "",
          "FAMILYCANCER-8":
            this.mapping(r, "2.癌症部分：-7.口腔癌", "1", "8") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "7")
              ? "8"
              : "",
          "FAMILYCANCER-9":
            this.mapping(r, "2.癌症部分：-8.攝護腺癌", "1", "9") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "8")
              ? "9"
              : "",
          "FAMILYCANCER-A":
            this.mapping(r, "2.癌症部分：-9.胰臟癌", "1", "A") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "9")
              ? "A"
              : "",
          "FAMILYCANCER-B":
            this.mapping(r, "2.癌症部分：-10.膽囊癌", "1", "B") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "10", "A")
              ? "B"
              : "",
          "FAMILYCANCER-C":
            this.mapping(r, "2.癌症部分：-11.卵巢癌", "1", "C") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "11", "B")
              ? "C"
              : "",
          "FAMILYCANCER-D":
            this.mapping(r, "2.癌症部分：-12.鼻咽癌", "1", "D") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "12", "C")
              ? "D"
              : "",
          "FAMILYCANCER-E":
            this.mapping(r, "2.癌症部分：-13.食道癌", "1", "E") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "13", "D")
              ? "E"
              : "",
          "FAMILYCANCER-F":
            this.mapping(r, "2.癌症部分：-14.骨癌", "1", "F") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "14", "E")
              ? "F"
              : "",
          "FAMILYCANCER-G":
            this.mapping(r, "2.癌症部分：-15.白血病(血癌)", "1", "G") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "15", "F")
              ? "G"
              : "",
          "FAMILYCANCER-H":
            this.mapping(
              r,
              "2.癌症部分：-16.遺傳性非瘜肉大腸直腸癌",
              "1",
              "H",
            ) || this.hasOptionCode(外部問卷, "2.癌症部分：", "16", "G")
              ? "H"
              : "",
          "FAMILYCANCER-I":
            this.mapping(r, "2.癌症部分：-17.其他癌症", "1", "I") ||
            this.hasOptionCode(外部問卷, "2.癌症部分：", "17", "H")
              ? "I"
              : "",
          FAMILYCANCEROTHER: this.mapping(r, "其他家族癌症名稱？"),
          "FAMILYSLOW-1":
            this.mapping(r, "1.慢性疾病部分：-0.無", "1", "1") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "0")
              ? "1"
              : "",
          "FAMILYSLOW-2":
            this.mapping(r, "1.慢性疾病部分：-1.高血壓", "1", "2") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "1")
              ? "2"
              : "",
          "FAMILYSLOW-3":
            this.mapping(r, "1.慢性疾病部分：-2.糖尿病", "1", "3") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "2")
              ? "3"
              : "",
          "FAMILYSLOW-4":
            this.mapping(r, "1.慢性疾病部分：-3.血脂異常", "1", "4") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "3")
              ? "4"
              : "",
          "FAMILYSLOW-5":
            this.mapping(r, "1.慢性疾病部分：-4.心臟病", "1", "5") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "4")
              ? "5"
              : "",
          "FAMILYSLOW-6":
            this.mapping(r, "1.慢性疾病部分：-5.腦中風", "1", "6") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "5")
              ? "6"
              : "",
          "FAMILYSLOW-7":
            this.mapping(r, "1.慢性疾病部分：-6.精神疾病", "1", "7") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "6")
              ? "7"
              : "",
          "FAMILYSLOW-8":
            this.mapping(r, "1.慢性疾病部分：-7.腎臟病", "1", "8") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "7")
              ? "8"
              : "",
          "FAMILYSLOW-9":
            this.mapping(r, "1.慢性疾病部分：-8.癲癇", "1", "9") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "8")
              ? "9"
              : "",
          "FAMILYSLOW-A":
            this.mapping(r, "1.慢性疾病部分：-9.B/C型肝炎帶原", "1", "A") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "9")
              ? "A"
              : "",
          "FAMILYSLOW-B":
            this.mapping(r, "1.慢性疾病部分：-10.慢性肝炎/肝硬化", "1", "B") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "10", "A")
              ? "B"
              : "",
          "FAMILYSLOW-C":
            this.mapping(r, "1.慢性疾病部分：-11.攝護腺肥大", "1", "C") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "11", "B")
              ? "C"
              : "",
          "FAMILYSLOW-D":
            this.mapping(r, "1.慢性疾病部分：-12.痛風", "1", "D") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "12", "C")
              ? "D"
              : "",
          "FAMILYSLOW-E":
            this.mapping(r, "1.慢性疾病部分：-13.巴金森氏症", "1", "E") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "13", "D")
              ? "E"
              : "",
          "FAMILYSLOW-F":
            this.mapping(r, "1.慢性疾病部分：-14.結核病", "1", "F") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "14", "E")
              ? "F"
              : "",
          "FAMILYSLOW-G":
            this.mapping(r, "1.慢性疾病部分：-15.氣喘", "1", "G") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "15", "F")
              ? "G"
              : "",
          "FAMILYSLOW-H":
            this.mapping(r, "1.慢性疾病部分：-16.大腸直腸瘜肉", "1", "H") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "16", "G")
              ? "H"
              : "",
          "FAMILYSLOW-I":
            this.mapping(r, "1.慢性疾病部分：-17.家族性大腸瘜肉症", "1", "I") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "17", "H")
              ? "I"
              : "",
          "FAMILYSLOW-J":
            this.mapping(r, "1.慢性疾病部分：-18.尿路結石", "1", "J") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "18", "I")
              ? "J"
              : "",
          "FAMILYSLOW-K":
            this.mapping(r, "1.慢性疾病部分：-19.其他", "1", "K") ||
            this.hasOptionCode(外部問卷, "1.慢性疾病部分：", "19", "J")
              ? "K"
              : "",
          FAMILYSLOWOTHER: this.mapping(r, "其他家族疾病？"),
          "SELFDISEASE-1":
            this.mapping(r, "1.疾病：-0.無", "1", "1") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "0")
              ? "1"
              : "",
          "SELFDISEASE-2":
            this.mapping(r, "1.疾病：-1.高血壓", "1", "2") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "1")
              ? "2"
              : "",
          "SELFDISEASE-3":
            this.mapping(r, "1.疾病：-2.糖尿病", "1", "3") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "2")
              ? "3"
              : "",
          "SELFDISEASE-4":
            this.mapping(r, "1.疾病：-3.高血脂症", "1", "4") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "3")
              ? "4"
              : "",
          "SELFDISEASE-5":
            this.mapping(r, "1.疾病：-4.心臟病", "1", "5") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "4")
              ? "5"
              : "",
          "SELFDISEASE-6":
            this.mapping(r, "1.疾病：-5.腦中風", "1", "6") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "5")
              ? "6"
              : "",
          "SELFDISEASE-7":
            this.mapping(r, "1.疾病：-6.腎臟病", "1", "7") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "6")
              ? "7"
              : "",
          "SELFDISEASE-8":
            this.mapping(r, "1.疾病：-7.B型肝炎", "1", "8") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "7")
              ? "8"
              : "",
          "SELFDISEASE-9":
            this.mapping(r, "1.疾病：-8.C型肝炎", "1", "9") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "8")
              ? "9"
              : "",
          "SELFDISEASE-A":
            this.mapping(r, "1.疾病：-9.精神疾病", "1", "A") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "9")
              ? "A"
              : "",
          "SELFDISEASE-B":
            this.mapping(r, "1.疾病：-10.痛風", "1", "B") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "10", "A")
              ? "B"
              : "",
          "SELFDISEASE-C":
            this.mapping(r, "1.疾病：-11.骨質疏鬆", "1", "C") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "11", "B")
              ? "C"
              : "",
          "SELFDISEASE-D":
            this.mapping(r, "1.疾病：-12.結核病", "1", "D") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "12", "C")
              ? "D"
              : "",
          "SELFDISEASE-E":
            this.mapping(r, "1.疾病：-13.氣喘", "1", "E") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "13", "D")
              ? "E"
              : "",
          "SELFDISEASE-F":
            this.mapping(r, "1.疾病：-14.慢性潰瘍結腸炎", "1", "F") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "14", "E")
              ? "F"
              : "",
          "SELFDISEASE-G":
            this.mapping(r, "1.疾病：-15.尿路結石", "1", "G") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "15", "F")
              ? "G"
              : "",
          "SELFDISEASE-H":
            this.mapping(r, "1.疾病：-16.家族性大腸瘜肉症(FAP)", "1", "H") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "16", "G")
              ? "H"
              : "",
          "SELFDISEASE-I":
            this.mapping(r, "1.疾病：-17.大腸直腸瘜肉", "1", "I") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "17", "H")
              ? "I"
              : "",
          "SELFDISEASE-J":
            this.mapping(r, "1.疾病：-18.遺傳性非瘜肉大腸直腸癌", "1", "J") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "18", "I")
              ? "J"
              : "",
          "SELFDISEASE-K":
            this.mapping(r, "1.疾病：-19.骨刺", "1", "K") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "19", "J")
              ? "K"
              : "",
          "SELFDISEASE-L":
            this.mapping(r, "1.疾病：-20.其他神經疾病", "1", "L") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "20", "K")
              ? "L"
              : "",
          "SELFDISEASE-M":
            this.mapping(r, "1.疾病：-21.癌症", "1", "M") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "21", "L")
              ? "M"
              : "",
          "SELFDISEASE-N":
            this.mapping(r, "1.疾病：-22.其他", "1", "N") ||
            this.hasOptionCode(外部問卷, "1.疾病：", "22", "M")
              ? "N"
              : "",
          SELFDISEASENURSE: this.mapping(r, "1-20.其他神經疾病名稱"),
          SELFDISEASECANCER: this.mapping(r, "1-21.癌症名稱"),
          SELFDISEASEOTHER: this.mapping(r, "1-22.其他個人疾病？"),
          TAKELONGMED: this.mapping(r, "2.長期服藥："),
          TAKELONGMEDREASON: this.mapping(r, "服藥原因？"),
          FEELNERVSE: this.mappingOption(r, "1.感覺緊張不安"),
          EASYANGERY: this.mappingOption(r, "2.覺得容易苦惱或動怒"),
          FEELDOWN: this.mappingOption(r, "3.感覺憂鬱、心情低落"),
          COMPAREOTHER: this.mappingOption(r, "4.覺得比不上別人"),
          SLEEPDIFFICULT: this.mappingOption(
            r,
            "5.睡眠困難，譬如難以入睡、易醒或早醒",
          ),
          LIVESITUATION: this.mapping(r, "1.請問您目前居住狀況："),
          LIVEOTHER: this.mapping(r, "其他居住情況？"),
          MARRAGE: this.mapping(r, "2.婚姻狀況："),
          MARRAGEOTHER: this.mapping(r, "其他婚姻情況？"),
          SLEEPSITUATION: this.mapping(r, "3.睡眠狀況："),
          SLEEPOTHER: this.mapping(r, "其他睡眠情況？"),
          "BIGEVENT-1": this.mapping(r, "4.一年內有無重大事件發生：", "1"),
          "BIGEVENT-2": this.mapping(r, "4.一年內有無重大事件發生：", "2"),
          "BIGEVENT-a":
            this.mapping(r, "4-1.一年內有重大事件-a.親友死亡", "1", "a") ||
            this.mapping(r, "4-1.一年內有重大事件", "a", "a"),
          "BIGEVENT-b":
            this.mapping(r, "4-1.一年內有重大事件-b.身體疾病", "1", "b") ||
            this.mapping(r, "4-1.一年內有重大事件", "b", "b"),
          "BIGEVENT-c":
            this.mapping(r, "4-1.一年內有重大事件-c.被騙錢", "1", "c") ||
            this.mapping(r, "4-1.一年內有重大事件", "c", "c"),
          "BIGEVENT-d":
            this.mapping(r, "4-1.一年內有重大事件-d.其他", "1", "d") ||
            this.mapping(r, "4-1.一年內有重大事件", "d", "d"),
          BIGEVENTOTHER: this.mapping(r, "其他重大事件？"),
          "DISEASEHISTORY-1": this.mapping(r, "5.疾病史：", "1"),
          "DISEASEHISTORY-2": this.mapping(r, "5.疾病史：", "2"),
          "DISEASEHISTORY-a":
            this.mapping(r, "5-1.有疾病史-a.癌症", "1", "a") ||
            this.mapping(r, "5-1.有疾病史", "a", "a"),
          "DISEASEHISTORY-b":
            this.mapping(
              r,
              "5-1.有疾病史-b.高血壓、糖尿病、心臟病",
              "1",
              "b",
            ) || this.mapping(r, "5-1.有疾病史", "b", "b"),
          "DISEASEHISTORY-c":
            this.mapping(r, "5-1.有疾病史-c.憂鬱症", "1", "c") ||
            this.mapping(r, "5-1.有疾病史", "c", "c"),
          "DISEASEHISTORY-d":
            this.mapping(r, "5-1.有疾病史-d.精神相關疾病", "1", "d") ||
            this.mapping(r, "5-1.有疾病史", "d", "d"),
          "DISEASEHISTORY-e":
            this.mapping(
              r,
              "5-1.有疾病史-e.其他疾病(請說明疾病名稱)",
              "1",
              "e",
            ) || this.mapping(r, "5-1.有疾病史", "e", "e"),
          DISEASEHISTORYOTHER: this.mapping(r, "其他疾病(請說明疾病名稱)"),
          LIFESAT:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 1.基本上，您對您的生活滿意嗎？",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 1.基本上，您對您的生活滿意嗎？",
              "是",
              "0",
            ),
          STAYHOME:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 2.您是否比較喜歡待在家裡而較不喜歡外出及不喜歡做新的事？",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 2.您是否比較喜歡待在家裡而較不喜歡外出及不喜歡做新的事？",
              "是",
              "0",
            ),
          FELLLIFE:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 3.您是否大部份時間精神都很好?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 3.您是否大部份時間精神都很好?",
              "是",
              "0",
            ),
          FELLNOTGOOD:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 4.您是否害怕將有不幸的事情發生在您身上嗎？",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 4.您是否害怕將有不幸的事情發生在您身上嗎？",
              "是",
              "0",
            ),
          LIVEGOOD:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 5.您是否覺得「現在還能活著」是很好的事?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 5.您是否覺得「現在還能活著」是很好的事?",
              "是",
              "0",
            ),
          FEELTIRED:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 6.您是否常常感到厭煩?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 6.您是否常常感到厭煩?",
              "是",
              "0",
            ),
          FELLUSELEFF:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 7.您是否常常感到無論做什麼，都沒有用?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 7.您是否常常感到無論做什麼，都沒有用?",
              "是",
              "0",
            ),
          FELLNOVALUE:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 8.您是否覺得您現在活得很沒有價值?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 8.您是否覺得您現在活得很沒有價值?",
              "是",
              "0",
            ),
          NOHOBBY:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 9.您是否減少很多的活動和嗜好？",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 9.您是否減少很多的活動和嗜好？",
              "是",
              "0",
            ),
          FEELEMPTY:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 10.您是否覺得您的生活很空虛?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 10.您是否覺得您的生活很空虛?",
              "是",
              "0",
            ),
          FEELHAPPY:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 11.您是否大部份的時間都感到快樂?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 11.您是否大部份的時間都感到快樂?",
              "是",
              "0",
            ),
          MEMORYBAD:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 12.您是否覺得您比大多數人有記憶不好的困擾？",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 12.您是否覺得您比大多數人有記憶不好的困擾？",
              "是",
              "0",
            ),
          FEELENERGY:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 13.您是否覺得精力很充沛?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 13.您是否覺得精力很充沛?",
              "是",
              "0",
            ),
          FEELNOHOPE:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 14.您是否感覺您現在的情況是沒有希望的?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 14.您是否感覺您現在的情況是沒有希望的?",
              "是",
              "0",
            ),
          FEELOTHERHAPPY:
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 15.您是否覺得大部份的人都比您幸福?",
              "否",
              "1",
            ) ||
            this.mapping(
              r,
              "6.憂鬱量表GDS-15 - 15.您是否覺得大部份的人都比您幸福?",
              "是",
              "0",
            ),
          TAKEGIFT: this.mapping(
            r,
            "1.判斷力的困難：例如落入圈套或騙局，財務上不好的決定，買了對受禮者不合宜的禮物。",
          ),
          HOBBY: this.mapping(r, "2.對活動或嗜好的興趣降低。"),
          REPEATQUESTION: this.mapping(r, "3.重複相同的問題、故事和陳述。"),
          LEARNTOOL: this.mapping(
            r,
            "4.在學習如何使用工具、設備和小器具上有困難。例如：電視、音響、冷氣機、洗衣機、熱水爐(器)、微波爐、遙控器。",
          ),
          FORGETYEAR: this.mapping(r, "5.忘記正確的月份和年份。"),
          COMPLEXQUESTION: this.mapping(
            r,
            "6.處理複雜的財務上有困難。例如：個人或家庭的收支平衡、所得稅、繳費單。",
          ),
          REMEMBER: this.mapping(r, "7.記住約會的時間有困難。"),
          CONTMEMORY: this.mapping(r, "8.有持續的思考和記憶方面的問題。"),
          FEMDISEASE: "",
          "GETDISEASE-0": "",
          "GETDISEASE-1": "",
          "GETDISEASE-2": "",
          GETDISEASEOTHER: "",
          FAMILYBREAST: "",
          MOTHERBREAST: "",
          MOTHERI000913: "",
          SISTERBREAST: "",
          SISTERI000913: "",
          DOUGHTERBREAST: "",
          DOUGHTERI000913: "",
          GMOTHERBREAST: "",
          GMOTHERI000913: "",
          GMOTHERBREAST1: "",
          GMOTHERI0009131: "",
          ANTIEBREAST: "",
          ANTIEI000913: "",
          ANTIEBREAST1: "",
          ANTIEI0009131: "",
          FIRSTPERIOD: "",
          STOPPERIOD: "",
          STOPPERIODAGE: "",
          STOPPERIONREASON: "",
          STOPPERIODOTHER: "",
          BIRTHTIMES: "",
          SELFFEED: "",
          FIRSTBIRTH: "",
          TAKEHORM: "",
          TAKEHORMAGE: "",
          TAKEHORMYEAR: "",
          TAKEHORMREASON: "",
          TAKEBIRTHCONTROL: "",
          BIRTHCONTROLAGE: "",
          BIRTHCONTROLYEAR: "",
          NURINE: "",
          MNA_NOREASON:
            外部問卷[
              "請問您最近3個月內因重大事件不適宜評估，如刻意減重、車禍…等"
            ]?.split("，")[1] || "",
          QUESTION9_A: this.mapping(
            r,
            "A 過去三個月內有沒有因為食慾不振、消化問題、咀嚼或吞嚥困難而減少食量?",
          ),
          QUESTION9_B: this.mapping(r, "B 過去三個月內體重下降的情況"),
          QUESTION9_C: this.mapping(r, "C 活動能力"),
          QUESTION9_D: this.mapping(
            r,
            "D 過去三個月內有沒有受到心理創傷或患上急性疾病?(已預先排定之手術不算)",
          ),
          QUESTION9_E: this.mapping(r, "E 精神心理問題"),
          QUESTION9_F1: this.mapping(
            r,
            "F1 身體質量指數(BMI) (公斤/米2, kg/m2)身高單位換算成公尺，體重單位為公斤計算方式：公斤÷公尺÷公尺例：160公分，50公斤，取至小數點後一位，以下四捨五入BMI為「50公斤÷1.6公尺÷1.6公尺=19.5」",
          ),
          QUESTION9_F2: "",
          LOSTWEIGHT: this.mapping(
            r,
            "1.非刻意減重狀況下，過去一年體重減少3公斤或5%以上?",
          ),
          STAND5TIMES: this.mapping(
            r,
            "2.無法在不用手支撐的情況下，從椅子上站起來五次?(15秒內)",
          ),
          LOSTINTEREST: this.mapping(
            r,
            "3.過去一週內，是否覺得提不起勁來做事?",
          ),
          KNOWACTIVITY: this.mapping(r, "1.請問您怎麼知道今天這項活動的？"),
          KNOWOTHER: this.mapping(r, "其他通知方式請說明？"),
          EDUCATION: this.mapping(r, "2.您目前的教育程度是："),
          HANDYCAP: this.mapping(r, "3.您目前是否持有身心障礙手冊？"),
          NATIVE: this.mapping(r, "4.您是否有具有原住民身分?"),
          ECONMIC: this.mapping(r, "5.您目前的經濟狀況?"),
          SATISIFY: this.mapping(r, "6.您對於今天的服務感到滿意嗎？"),
          SATISIFYREASON: this.mapping(r, "滿意或不滿意的原因？"),
          JOINAGAIN: this.mapping(
            r,
            "7.如果以後我們有類似的檢查活動，您會再次參加嗎？",
          ),
          JOINAGAINREASON: this.mapping(r, "為什麼會/不會再次參加？"),
          INTRODUCE: this.mapping(r, "8.您會介紹親朋好友來參加我們的活動嗎？"),
          INTRODUCEREASON: this.mapping(r, "為什麼會/不會再次參加？"),
          IMPROVE: this.mapping(r, "9.您認為此次活動有需要改進的地方嗎?"),
          SATISIFY: this.mappingQuestion(r, "6.您對於今天的服務感到滿意嗎？"),
          SATISIFYREASON: this.mappingQuestion(r, "滿意或不滿意的原因？"),
          JOINAGAIN: this.mappingQuestion(
            r,
            "7.如果以後我們有類似的檢查活動，您會再次參加嗎？",
          ),
          JOINAGAINREASON: this.mappingQuestion(r, "為什麼會/不會再次參加？"),
          INTRODUCE: this.mappingQuestion(
            r,
            "8.您會介紹親朋好友來參加我們的活動嗎？",
          ),
          INTRODUCEREASON: this.mappingQuestion(r, "為什麼會/不會再次參加？"),
          IMPROVE: this.mappingQuestion(
            r,
            "9.您認為此次活動有需要改進的地方嗎?",
          ),
          SWALLOW_1: this.mapping(r, "1-1.吞嚥問題讓我的體重減輕"),
          SWALLOW_2: this.mapping(r, "1-2.因為吞嚥問題不能在外面用餐"),
          SWALLOW_3: this.mapping(r, "1-3.我喝飲料/水很費力"),
          SWALLOW_4: this.mapping(r, "1-4.我吃固體食物很費力"),
          SWALLOW_5: this.mapping(r, "1-5.我吞藥丸很費力"),
          SWALLOW_6: this.mapping(r, "1-6.吞嚥會感覺到痛"),
          SWALLOW_7: this.mapping(r, "1-7.因為吞嚥問題不能享受用餐"),
          SWALLOW_8: this.mapping(r, "1-8.吞嚥後感覺喉嚨有食物卡著"),
          SWALLOW_9: this.mapping(r, "1-9.當我進食的時候會咳嗽"),
          SWALLOW_10: this.mapping(r, "1-10.吞嚥讓我感覺緊張有壓力"),
          SWALLOW_TOTAL: this.mapSum(r, [
            "1-1.吞嚥問題讓我的體重減輕",
            "1-2.因為吞嚥問題不能在外面用餐",
            "1-3.我喝飲料/水很費力",
            "1-4.我吃固體食物很費力",
            "1-5.我吞藥丸很費力",
            "1-6.吞嚥會感覺到痛",
            "1-7.因為吞嚥問題不能享受用餐",
            "1-8.吞嚥後感覺喉嚨有食物卡著",
            "1-9.當我進食的時候會咳嗽",
            "1-10.吞嚥讓我感覺緊張有壓力",
          ]),
          CHEW_1: this.plusMapping(r, "2-1.硬豆干"),
          CHEW_2: this.plusMapping(r, "2-2.炒花生"),
          CHEW_3: this.plusMapping(r, "2-3.芭樂(整顆)"),
          CHEW_4: this.plusMapping(r, "2-4.炸雞"),
          CHEW_5: this.plusMapping(r, "2-5.水煮玉米(整枝)"),
          CHEW_6: this.plusMapping(r, "2-6.蘋果/梨子/蓮霧/芭樂(切片)"),
          CHEW_7: this.plusMapping(r, "2-7.烤魷魚/雞胗"),
          CHEW_8: this.plusMapping(r, "2-8.水煮花枝/滷豬耳朵"),
          CHEW_9: this.plusMapping(r, "2-9.柳丁(有切片)"),
          CHEW_10: this.plusMapping(r, "2-10.竹筍/敏豆/花椰菜/切片的小黃瓜"),
          CHEW_11: this.plusMapping(r, "2-11.煮熟的紅/白蘿蔔"),
          CHEW_TOTAL: this.plusSum(r, [
            "2-1.硬豆干",
            "2-2.炒花生",
            "2-3.芭樂(整顆)",
            "2-4.炸雞",
            "2-5.水煮玉米(整枝)",
            "2-6.蘋果/梨子/蓮霧/芭樂(切片)",
            "2-7.烤魷魚/雞胗",
            "2-8.水煮花枝/滷豬耳朵",
            "2-9.柳丁(有切片)",
            "2-10.竹筍/敏豆/花椰菜/切片的小黃瓜",
            "2-11.煮熟的紅/白蘿蔔",
          ]),
          APPOINTMENT: this.mapping(
            r,
            "3.請問您最近1年內有無因高血壓、高血脂或糖尿病在醫療院所規律就診？",
          ),
        };
      });
      // console.log(data);
      downloadCsv(
        this.sanitizeCsvRows(data),
        {},
        `${this.selected.場次.日期} ${this.selected.場次.地點}整篩問卷`,
      );
    },

    exportPrint(type) {
      if (!this.isSelectedSection()) {
        return false;
      }
      this.printDialog = true;
      this.printType = type;
    },

    scanHandler(value, e) {
      if (!value) return;
      if (
        // document.activeElement.id != "keyword" &&
        /^[A-Z](1|2)\d{8}$/i.test(value)
      ) {
        this.selected.關鍵字 = value;
        let finded = this.list.find((o) => o.身分證字號 == value);
        if (finded) {
          if (!finded.回檢) {
            finded.回檢 = true;
            this.toggleCheckin(finded);
            this.$toast.success(
              `${finded.身分證字號}(${finded.姓名}) 回檢完成`,
            );
          } else {
            this.$toast.error(
              `${finded.身分證字號}(${finded.姓名}) 已回檢完畢`,
            );
          }
        } else {
          this.$toast.error(`本場次找不到此民眾！`);
        }
      }
    },
    async load() {
      if (
        this.selected.行政區 instanceof Object &&
        !this.selected.行政區.value
      ) {
        this.selected.行政區 = null;
      }
      let params = {
        年度: this.selected.年度,
        行政區: this.selected.行政區?.value || this.selected.行政區,
      };
      this.sections = (
        await this.axios.get("/dashboard/section", {
          params,
        })
      ).data;
    },
    async loadList() {
      if (!this.isSelectedSection()) {
        return false;
      }
      let params = this.selected.場次;

      let list = (
        await this.axios.get(`/dashboard/person/list`, {
          params: { query: JSON.stringify(params) },
        })
      ).data;
      list = list.map((o) => {
        return {
          ...o,
          血液報告: this.getReport(o.report[0]),
        };
      });
      this.list = _.sortBy(list, ["報到號碼"]);
    },
    getReport(report) {
      let r = report.血液檢查;
      if (!r) return {};
      let result = {
        血壓: {
          high: false,
          low: false,
          abnormal: [],
        },
        血脂: {
          high: false,
          low: false,
          abnormal: [],
        },
        血糖: {
          high: false,
          low: false,
          abnormal: [],
        },
        肝: {
          high: false,
          low: false,
          abnormal: [],
        },
        腎: {
          high: false,
          low: false,
          abnormal: [],
        },
        raw: {},
      };

      result = this.calcItem(
        result,
        "收縮壓",
        "血壓",
        report.第一次收縮壓,
        parseInt,
        0,
        120,
      );
      result = this.calcItem(
        result,
        "收縮壓",
        "血壓",
        report.第二次收縮壓,
        parseInt,
        0,
        120,
      );
      result = this.calcItem(
        result,
        "舒張壓",
        "血壓",
        report.第一次舒張壓,
        parseInt,
        0,
        80,
      );
      result = this.calcItem(
        result,
        "舒張壓",
        "血壓",
        report.第二次舒張壓,
        parseInt,
        0,
        80,
      );
      result = this.calcItem(result, "GLU", "血糖", r.GLU, parseInt, 70, 100);
      result = this.calcItem(result, "SGOT", "肝", r.SGOT, parseInt, 0, 40);
      result = this.calcItem(result, "SGPT", "肝", r.SGPT, parseInt, 0, 40);
      result = this.calcItem(
        result,
        "CHOL",
        "血脂",
        r.CHOL,
        parseInt,
        0,
        199.9,
      );
      result = this.calcItem(result, "TG", "血脂", r.TG, parseInt, 0, 149.9);
      result = this.calcItem(
        result,
        "HDL-C",
        "血脂",
        r["HDL-C"],
        parseInt,
        0,
        r.性別 == "男" ? 39.9 : 49.9,
      );
      result = this.calcItem(
        result,
        "LDL-C",
        "血脂",
        r["LDL-C"],
        parseInt,
        0,
        130,
      );
      result = this.calcItem(
        result,
        "HBsAg",
        "肝",
        r.HBsAg,
        parseFloat,
        0,
        0.049,
      );
      result = this.calcItem(result, "HCV", "肝", r.HCV, parseFloat, 0, 0.99);
      result = this.calcItem(result, "AFP", "肝", r.AFP, parseFloat, 0, 19.99);
      if (r.腎臟病期別 != "正常") {
        result.腎.abnormal.push({
          item: "腎臟病期別",
          value: r.腎臟病期別,
        });
        result.腎.abnormal.push({
          item: "eGFR",
          value: r.eGFR,
        });
        result.腎.abnormal.push({
          item: "CREA",
          value: r.CREA,
        });
        result.腎.abnormal.push({
          item: "BUN",
          value: r.BUN,
        });
        result.腎.abnormal.push({
          item: "蛋白質",
          value: r.蛋白質,
        });
        result.腎.abnormal.push({
          item: "潛血",
          value: r.潛血,
        });
      }
      result.raw.eGFR = r.eGFR;
      result.raw.CREA = r.CREA;
      result.raw.BUN = r.BUN;
      result.raw.蛋白質 = r.蛋白質;
      result.raw.潛血 = r.潛血;
      result.raw.腎臟病期別 = r.腎臟病期別;
      result.raw.fobt = r.fobt;

      return result;
    },
    calcItem(result, key, type, value, fun, low, high) {
      result.raw[key] = value;
      if (value == undefined) return result;
      if (typeof value == "string") value = value.replace(/[^0-9.+-]/g, "");
      value = fun(value);

      if (value > high) {
        result[type].high = true;
        result[type].abnormal.push({
          item: key,
          value,
        });
      }

      if (value < low) {
        result[type].low = true;
        result[type].abnormal.push({
          item: key,
          value,
        });
      }

      return result;
    },
    async toggleCheckin(person) {
      let result;
      try {
        result = (
          await this.axios.post(
            "/dashboard/person/toggleCheckinReport",
            _.pick(person, ["_id", "回檢"]),
          )
        ).data;
      } catch (e) {
        if (person.回檢) {
          this.$toast.error(
            "請注意！目前網路連線失敗，報到動作沒有傳送至伺服器！",
          );
        }
      }

      if (result.回檢時間) {
        person.回檢時間 = result.回檢時間;
        this.$forceUpdate();
      }
    },
  },
  watch: {
    "selected.行政區"() {
      this.load();
    },
    "selected.年度"() {
      this.load();
    },
    "selected.場次"() {
      this.loadList();
    },
  },
};
</script>

<style lang="scss" scoped></style>
